import {
  useProfessionalCreditStatus,
  useProfessionalEditionGuardedImport
} from '@common/credits/ProfessionalEdition/hooks';
import { PLAN_TYPES } from '@common/credits/ProfessionalEdition/types';
import { StyledTableLink } from '@common/Dependency/styled';
import { EntityIcon } from '@common/Entity/EntityIcons';
import { SnowflakePwdAuthBanner } from '@common/Fabric/SnowflakePwdAuthBanner';
import { ProjectHeader } from '@common/Header/PageHeader';
import { Entity } from '@common/types/Entity';
import { Private_Routes } from '@common/url';
import { renderColumnWithEllipsis, renderTimeColumnWithEllipsis } from '@common/utils/renderUtils';
import { InfoCircleIcon, RepeatAIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import {
  Badges,
  Button,
  Centered,
  ColumnsType,
  Ellipsis,
  MetadataPageContainer,
  SortOrder,
  Stack,
  StackItem,
  Table,
  TableActions,
  TableIconButton,
  Text
} from '@prophecy/ui-v3';
import { ProgressBar } from '@prophecy/ui-v3/ProgressBar';
import DocumentTitle from '@prophecy/utils/react/DocumentTitle';
import { isCaseInsensitiveEqual } from '@prophecy/utils/string';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useImmer } from 'use-immer';

import ImageTranspiler from '../../assets/transpiler_gems.svg';
import { fetchPackagesList } from '../../data/apis/api';
import { useRestQuery } from '../../data/util';
import { DeleteTranspiler } from './components/DeleteTranspiler';
import { ListingHeader } from './components/ListingHeader';
import {
  FileFormatLabels,
  FileFormats,
  ImportType,
  ListingHeaderType,
  QueryKey,
  StatusMapKeys,
  Transpiler_Headline,
  TranspilerVersionType
} from './config';
import { ImportStepValue } from './Import/steps/types';
import { getStatusColumn } from './ImportedInfo/util';

const StyledCard = styled.div`
  background-color: ${theme.colors.brand25};
  background-image: url(${ImageTranspiler});
  background-position: right top;
  background-size: auto 100%;
  background-repeat: no-repeat;
  padding: ${theme.spaces.x40} ${theme.spaces.x48};
  border-radius: ${theme.radius.x24};
`;

export default function Listing() {
  const [{ searchName = '', projectName, source }, setHeaderData] = useImmer<ListingHeaderType>({});
  const navigate = useNavigate();
  const guardImport = useProfessionalEditionGuardedImport();
  const { plan, maxTranspilerImportsPerMonth, transpilerImportsCurrentMonth, billingEndDate } =
    useProfessionalCreditStatus();
  const isProfessionalEdition = plan === PLAN_TYPES.Pro;

  const isImportLimitReached =
    maxTranspilerImportsPerMonth > 0 && transpilerImportsCurrentMonth >= maxTranspilerImportsPerMonth;

  const handleImportBundleClick = guardImport(() =>
    navigate(Private_Routes.TranspilerImport.home.getUrl(undefined, undefined))
  );

  const columns: ColumnsType<ImportType> = useMemo(
    () => [
      {
        key: 'name',
        dataIndex: 'name',
        title: 'Bundle Name',
        sortable: true,
        render: (name: string, rowData) => {
          const version = rowData?.isBulk ? TranspilerVersionType.v2 : TranspilerVersionType.v1;
          const isTranspiled = isCaseInsensitiveEqual(rowData.status, StatusMapKeys.imported);
          if (!isTranspiled && rowData.isBulk) {
            return renderColumnWithEllipsis(name);
          }
          return (
            <StyledTableLink
              to={Private_Routes.TranspilerDetail.getUrl({
                uid: rowData?.transpilerId,
                version,
                type: rowData.transpilerType
              })}
              data-test-id='listing-transpiler-link'>
              {renderColumnWithEllipsis(name)}
            </StyledTableLink>
          );
        }
      },
      {
        key: 'transpilerType',
        dataIndex: 'transpilerType',
        title: 'Source System',
        sortable: true,
        width: '250px',
        render: (source) => {
          return (
            <Stack direction='horizontal' gap={theme.spaces.x6} width='100%' alignY='center'>
              <Ellipsis tooltip>{FileFormatLabels[source as FileFormats]}</Ellipsis>
            </Stack>
          );
        }
      },
      getStatusColumn(true),
      {
        key: 'teamName',
        dataIndex: 'teamName',
        title: 'Team',
        sortable: true,
        render: renderColumnWithEllipsis
      },
      {
        key: 'projectName',
        dataIndex: 'projectName',
        title: 'Project',
        sortable: true,
        render: renderColumnWithEllipsis
      },
      {
        key: 'importedAt',
        dataIndex: 'importedAt',
        title: 'Import Date',
        sortable: true,
        render: renderTimeColumnWithEllipsis
      },
      {
        align: 'center',
        dataIndex: 'actions',
        width: '80px',
        key: 'actions',
        resizable: false,
        render: (_: string, record) => {
          const transpilerId = record?.transpilerId;
          const isTranspiled =
            isCaseInsensitiveEqual(record.status, StatusMapKeys.completed) ||
            isCaseInsensitiveEqual(record.status, StatusMapKeys.running) ||
            isCaseInsensitiveEqual(record.status, StatusMapKeys.error) ||
            isCaseInsensitiveEqual(record.status, StatusMapKeys.failed);
          const isAnalyzing = isCaseInsensitiveEqual(record.status, StatusMapKeys.analyzing);
          const isCancelling = isCaseInsensitiveEqual(record.status, StatusMapKeys.cancelling);
          const handleReimportClick = guardImport(() =>
            navigate(
              Private_Routes.TranspilerImport.tab.getUrl(
                { transpileId: transpilerId },
                {
                  source: record.transpilerType || FileFormats.abinitio,
                  step: `${isTranspiled ? ImportStepValue.TranspileGraph : isAnalyzing ? ImportStepValue.AnalyzeGraph : ImportStepValue.Sources}`
                }
              )
            )
          );
          return (
            <TableActions align='center'>
              {record?.isBulk && !isCancelling && !isProfessionalEdition && (
                <TableIconButton
                  tooltip='Reimport Pipeline'
                  icon={<RepeatAIcon type='default' color={theme.colors.gray500} />}
                  onClick={handleReimportClick}
                  aria-label={`Reimport ${record.name}`}
                  data-test-id='listing-reimport-button'
                />
              )}
              <DeleteTranspiler transpilerId={transpilerId} name={record?.name} isBulk={record?.isBulk} />
            </TableActions>
          );
        }
      }
    ],
    [guardImport, navigate, isProfessionalEdition]
  );

  const { data, isLoading, isError } = useRestQuery([QueryKey, 'Listing'], () => fetchPackagesList());

  const importData = useMemo(() => {
    let filteredData = data?.data?.imports || [];
    if (searchName) {
      filteredData = filteredData.filter((record) => record.name.toLowerCase().indexOf(searchName?.toLowerCase()) > -1);
    }
    if (projectName) {
      filteredData = filteredData.filter((record) => record.projectId === projectName);
    }
    if (source) {
      filteredData = filteredData.filter((record) => record.transpilerType === source);
    }
    return filteredData;
  }, [data?.data, searchName, projectName, source]);

  const onChange = (key: keyof ListingHeaderType, value = '') => {
    setHeaderData((draft) => {
      draft[key] = value;
    });
  };

  if (isError) {
    return (
      <Centered>
        <Badges tone='error'>Unable to fetch Bundles list</Badges>
      </Centered>
    );
  }

  const header = (
    <ProjectHeader
      crumbs={[
        {
          icon: <EntityIcon size='m' entity={Entity.Transpiler} />,
          elementType: 'link',
          children: Transpiler_Headline,
          to: Private_Routes.Transpiler.getUrl(),
          'data-test-id': 'transpiler-listing-header-crumb'
        }
      ]}
    />
  );
  const maincontent = (
    <Stack width='100%' height='100%' gap={theme.spaces.x40}>
      <DocumentTitle title='Import Listing' />
      <StyledCard>
        <Stack gap={theme.spaces.x20}>
          <Text level='sm' tone={theme.colors.purple700} weight={theme.fontWeight.semiBold}>
            Prophecy Transpiler
          </Text>
          <Text level='xl' tone={theme.colors.gray900} weight={theme.fontWeight.medium}>
            End-to-End data automation, reliability, and scalability
          </Text>
          <StyledImportDescription>
            <Text level='sm' tone={theme.colors.gray600}>
              Centralize all your data sources with Prophecy import technology, converting multiple Legacy ETL products
              into a single streamlined architecture for all data assets and users.
            </Text>
          </StyledImportDescription>
          {isProfessionalEdition ? (
            <StyledImportLimitContainer direction='vertical' gap={theme.spaces.x12} width='810px'>
              <Stack
                direction='vertical'
                gap={theme.spaces.x4}
                align='space-between'
                width='100%'
                padding={theme.spaces.x24}>
                <Stack direction='horizontal' gap={theme.spaces.x12} width='100%' alignY='center'>
                  <Text level='sm' tone={theme.colors.gray600} weight={theme.fontWeight.medium}>
                    Imports this month
                  </Text>
                  <Text level='xs' tone={theme.colors.gray500}>
                    (Resets on {billingEndDate})
                  </Text>
                </Stack>
                <Stack direction='horizontal' gap={theme.spaces.x12} align='space-between'>
                  <Stack direction='horizontal' gap={theme.spaces.x24} width='290px' alignY='center'>
                    <Text level='lg' tone={theme.colors.gray600} weight={theme.fontWeight.medium}>
                      {transpilerImportsCurrentMonth}
                      <Text level='sm' tone={theme.colors.gray600} weight={theme.fontWeight.semiBold}>
                        /{maxTranspilerImportsPerMonth}
                      </Text>
                    </Text>
                    <ProgressBar
                      value={(transpilerImportsCurrentMonth / maxTranspilerImportsPerMonth) * 100}
                      variant={isImportLimitReached ? 'error' : 'success'}
                      height={theme.sizes.x8}
                      width='100%'
                    />
                  </Stack>
                  <Stack gap={theme.spaces.x20} direction='horizontal'>
                    <StyledButton
                      variant='secondaryGray'
                      elementType='anchor'
                      rel='noreferrer'
                      target='_blank'
                      disablePointerFocus
                      href='https://transpiler.docs.prophecy.io/'
                      data-test-id='listing-learn-more-button'>
                      Learn More
                    </StyledButton>
                    <StyledButton
                      variant='primary'
                      onClick={handleImportBundleClick}
                      data-test-id='listing-import-bundle-button'
                      disabled={isImportLimitReached}
                      tooltip={
                        isImportLimitReached
                          ? `You have reached your monthly import limit. No more imports available until ${billingEndDate}.`
                          : undefined
                      }>
                      Import Bundle
                    </StyledButton>
                  </Stack>
                </Stack>
              </Stack>
              {isImportLimitReached && (
                <StyledImportLimitReachedBanner direction='horizontal' gap={theme.spaces.x12} alignY='center'>
                  <InfoCircleIcon type='default' color={theme.colors.error500} />
                  <Text level='sm' tone={theme.colors.gray700} weight={theme.fontWeight.medium}>
                    You have reached your monthly import limit. No more imports available until {billingEndDate}.
                  </Text>
                </StyledImportLimitReachedBanner>
              )}
            </StyledImportLimitContainer>
          ) : (
            <Stack gap={theme.spaces.x20} direction='horizontal'>
              <StyledButton
                variant='secondaryGray'
                elementType='anchor'
                rel='noreferrer'
                target='_blank'
                disablePointerFocus
                href='https://transpiler.docs.prophecy.io/'
                data-test-id='listing-learn-more-button'>
                Learn More
              </StyledButton>
              <StyledButton
                variant='primary'
                onClick={handleImportBundleClick}
                data-test-id='listing-import-bundle-button'>
                Import Bundle
              </StyledButton>
            </Stack>
          )}
        </Stack>
      </StyledCard>
      <ListingHeader projectName={projectName} searchName={searchName} source={source} onChange={onChange} />
      <Stack grow='1' padding={`0 0 ${theme.spaces.x40}`}>
        <Table
          loading={isLoading}
          virtualize={true}
          height='100%'
          width='100%'
          showEmptyMessage={{ description: 'No imported bundles' }}
          columns={columns}
          dataSource={importData}
          data-test-id='transpiler-imported-bundles-table'
          sortConfig={{ sortOrder: SortOrder.desc, sortColumn: 'importedAt' }}
        />
      </Stack>
    </Stack>
  );
  return (
    <MetadataPageContainer
      banner={<SnowflakePwdAuthBanner />}
      header={header}
      main={maincontent}></MetadataPageContainer>
  );
}

const StyledImportLimitContainer = styled(Stack)`
  background-color: ${theme.colors.white};
  border-radius: ${theme.radius.x12};
  width: 810px;
`;

const StyledImportLimitReachedBanner = styled(Stack)`
  background-color: ${theme.colors.white};
  width: 810px;
  border-top: 1px solid ${theme.colors.gray200};
  padding: ${theme.spaces.x12};
  padding-left: ${theme.spaces.x24};
`;

const StyledButton = styled(Button)`
  padding: ${theme.spaces.x10} ${theme.spaces.x14};
  width: 9.75rem;
`;

const StyledImportDescription = styled(StackItem)`
  width: 650px;
`;
